import React from 'react';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { ReportExportButtons } from './ReportExportButtons';

export interface ReportPageProps {
  /** Filter controls (e.g. year picker) rendered left of export buttons */
  filters: React.ReactNode;
  onExportExcel: () => Promise<void>;
  onExportPDF: () => Promise<void>;
  exportDisabled?: boolean;
  loading: boolean;
  error: boolean;
  errorMessage?: string;
  children: React.ReactNode;
}

export const ReportPage: React.FC<ReportPageProps> = ({
  filters,
  onExportExcel,
  onExportPDF,
  exportDisabled = false,
  loading,
  error,
  errorMessage = 'Error loading report. Please try again.',
  children,
}) => {
  const { colors } = useTheme();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="large" message="Loading report..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className={cn('border-l-4 rounded-lg p-4 shadow-sm', colors.error)}>
          <div className="flex items-center">
            <svg className={cn('w-5 h-5 mr-3', colors.text)} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p className={cn('font-medium', colors.text)}>{errorMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('p-3 h-full flex flex-col min-h-0', colors.background)}>
      <div className="mb-2 flex-shrink-0 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          {filters}
        </div>
        <ReportExportButtons
          onExportExcel={onExportExcel}
          onExportPDF={onExportPDF}
          disabled={exportDisabled}
        />
      </div>
      <div className="flex-1 overflow-auto">
        {children}
      </div>
    </div>
  );
};
